import {format} from 'date-fns';
import {combine, merge, sample} from 'effector';
import {createGate} from 'effector-react';

import {showRestError} from '@app/errors';
import {rootDomain} from '@app/model';
import {dateModel} from '@entities/date/model';
import {createGridTableModel, initFiltersTable, initSortingsTable} from '@shared/composables/table';
import {InnerFilters} from '@shared/types/grid';
import {SelectOption} from '@shared/ui/Select';

import * as api from './api';
import {
    DictionarySearchResponse,
    NotificationContentDto,
    NotificationDetailsResponse,
    NotificationDictionaryItem,
    NotificationDto,
    NotificationFilterNode,
    NotificationMessageDto,
    NotificationsRequestParams,
} from './types';

const domain = rootDomain.createDomain('Notifications');

export const NotificationsGate = createGate<{tin?: string; violationId?: string}>('Notifications');

const fetchNotificationsFx = domain.createEffect({handler: api.fetchNotifications});
const fetchNotificationKindsFx = domain.createEffect({handler: api.fetchNotificationKinds});
const fetchNotificationStatusesFx = domain.createEffect({handler: api.fetchNotificationStatuses});
const fetchViolationTypesFx = domain.createEffect({handler: api.fetchViolationTypes});
const fetchNotificationDetailsFx = domain.createEffect({handler: api.fetchNotificationDetails});
const downloadNotificationMessageFx = domain.createEffect({
    handler: api.downloadNotificationMessage,
});
const downloadNotificationContentFx = domain.createEffect({
    handler: api.downloadNotificationContent,
});

[
    fetchNotificationsFx,
    fetchNotificationKindsFx,
    fetchNotificationStatusesFx,
    fetchViolationTypesFx,
    fetchNotificationDetailsFx,
    downloadNotificationMessageFx,
    downloadNotificationContentFx,
].forEach((fx) => fx.failData.watch(showRestError));

const grid = createGridTableModel<{tin?: string; violationId?: string}>({
    domain,
    Gate: NotificationsGate,
    defaultSortings: {created: true},
});

export const {
    filters$,
    filtersChanged,
    defaultFilters,
    filtersReset,
    sortings$,
    sortingsChanged,
    defaultSortings,
    sortingsReset,
    pageNumber$,
    pageNumberChanged,
    pageSize$,
    pageSizeChanged,
    tableStateChanged,
    stateReset,
} = grid;

initFiltersTable(filters$, filtersChanged, filtersReset, defaultFilters, stateReset);
initSortingsTable(sortings$, sortingsChanged, sortingsReset, defaultSortings, stateReset);

pageNumber$.reset(pageSizeChanged, stateReset);
pageSize$.reset(stateReset);

const detailsOpened = domain.createEvent<string>();
const detailsClosed = domain.createEvent();
const notificationFileDownloadRequested = domain.createEvent<string>();
const attachmentDownloadRequested = domain.createEvent<string>();
const selectedNotificationChanged = domain.createEvent<NotificationDto | null>();

const data$ = domain
    .createStore<Awaited<ReturnType<typeof api.fetchNotifications>> | null>(null)
    .on(fetchNotificationsFx.doneData, (_, data) => data)
    .reset(NotificationsGate.close);

const notificationKinds$ = domain
    .createStore<NotificationDictionaryItem[]>([])
    .on(fetchNotificationKindsFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(NotificationsGate.close);

const notificationStatuses$ = domain
    .createStore<NotificationDictionaryItem[]>([])
    .on(
        fetchNotificationStatusesFx.doneData,
        (_, data: DictionarySearchResponse) => data.data ?? [],
    )
    .reset(NotificationsGate.close);

const violationTypes$ = domain
    .createStore<NotificationDictionaryItem[]>([])
    .on(fetchViolationTypesFx.doneData, (_, data: DictionarySearchResponse) => data.data ?? [])
    .reset(NotificationsGate.close);

const selectedNotification$ = domain
    .createStore<NotificationDto | null>(null)
    .on(selectedNotificationChanged, (_, value) => value)
    .reset(detailsClosed, NotificationsGate.close);

const selectedMessage$ = domain
    .createStore<NotificationMessageDto | null>(null)
    .on(fetchNotificationDetailsFx.doneData, (_, data: NotificationDetailsResponse) => data.message)
    .reset(detailsOpened, detailsClosed, NotificationsGate.close);

const contentList$ = domain
    .createStore<NotificationContentDto[]>([])
    .on(
        fetchNotificationDetailsFx.doneData,
        (_, data: NotificationDetailsResponse) => data.contents,
    )
    .reset(detailsOpened, detailsClosed, NotificationsGate.close);

const isDetailsOpen$ = domain
    .createStore(false)
    .on(detailsOpened, () => true)
    .on(detailsClosed, () => false)
    .reset(NotificationsGate.close);

const getSelectedCode = (value: InnerFilters[string]) =>
    ((value as SelectOption<string> | null | undefined)?.value ?? null) as string | null;

const buildFilter = ({
    tin,
    violationId,
    from,
    to,
    kindCode,
    statusCode,
}: Omit<NotificationsRequestParams, 'sortings' | 'page' | 'size'>): NotificationFilterNode => {
    const nodes: NotificationFilterNode[] = [];

    if (tin) nodes.push({type: 'EQ', field: 'tin', values: [tin]});
    if (violationId) nodes.push({type: 'EQ', field: 'violationId', values: [violationId]});

    const fromPeriod = from ? format(from, 'yyyyMM') : null;
    const toPeriod = to ? format(to, 'yyyyMM') : null;

    if (fromPeriod && toPeriod) {
        nodes.push(
            fromPeriod === toPeriod
                ? {type: 'EQ', field: 'taxPeriod', values: [fromPeriod]}
                : {type: 'BTW', field: 'taxPeriod', values: [fromPeriod, toPeriod]},
        );
    }

    if (kindCode) {
        nodes.push({type: 'EQ', field: 'kind.code', values: [kindCode]});
    }

    if (statusCode) {
        nodes.push({type: 'EQ', field: 'status.code', values: [statusCode]});
    }

    return {type: 'AND', children: nodes};
};

const mapSorts = (sortings: Record<string, boolean | undefined>) => {
    const [field, value] = Object.entries(sortings)[0] ?? [];
    if (!field || value == null) return null;
    return [{field, order: value ? 'DESC' : 'ASC'} as const];
};

sample({
    clock: NotificationsGate.open,
    target: [fetchNotificationKindsFx, fetchNotificationStatusesFx, fetchViolationTypesFx],
});

sample({
    clock: merge([NotificationsGate.open, tableStateChanged, dateModel.changed]),
    source: combine({
        gate: NotificationsGate.state,
        date: dateModel.state,
        filters: filters$,
        sortings: sortings$,
        pageNumber: pageNumber$,
        pageSize: pageSize$,
    }),
    filter: NotificationsGate.status,
    fn: ({gate, date, filters, sortings, pageNumber, pageSize}) => ({
        filter: buildFilter({
            tin: gate.tin,
            violationId: gate.violationId,
            from: date.range.from,
            to: date.range.to,
            kindCode: getSelectedCode(filters.kind),
            statusCode: getSelectedCode(filters.status),
        }),
        sorts: mapSorts(sortings),
        page: {
            page: pageNumber,
            size: pageSize,
        },
    }),
    target: fetchNotificationsFx,
});

sample({
    clock: detailsOpened,
    source: data$,
    fn: (data, notificationId) => data?.data?.find((item) => item.id === notificationId) ?? null,
    target: selectedNotificationChanged,
});

sample({
    clock: detailsOpened,
    target: fetchNotificationDetailsFx,
});

sample({
    clock: notificationFileDownloadRequested,
    target: downloadNotificationMessageFx,
});

sample({
    clock: attachmentDownloadRequested,
    target: downloadNotificationContentFx,
});

const $notificationsState = combine({
    data: data$,
    filters: filters$,
    sortings: sortings$,
    pageNumber: pageNumber$,
    pageSize: pageSize$,
    notificationKinds: notificationKinds$,
    notificationStatuses: notificationStatuses$,
    violationTypes: violationTypes$,
    selectedNotification: selectedNotification$,
    selectedMessage: selectedMessage$,
    contentList: contentList$,
    isDetailsOpen: isDetailsOpen$,
    pending: combine(
        [
            fetchNotificationsFx.pending,
            fetchNotificationKindsFx.pending,
            fetchNotificationStatusesFx.pending,
            fetchViolationTypesFx.pending,
        ],
        (list) => list.some(Boolean),
    ),
    detailsPending: combine(
        [
            fetchNotificationDetailsFx.pending,
            downloadNotificationMessageFx.pending,
            downloadNotificationContentFx.pending,
        ],
        (list) => list.some(Boolean),
    ),
});

export const notificationsModel = {
    state: $notificationsState,
    filtersChanged,
    filtersReset,
    sortingsChanged,
    sortingsReset,
    pageNumberChanged,
    pageSizeChanged,
    detailsOpened,
    detailsClosed,
    notificationFileDownloadRequested,
    attachmentDownloadRequested,
};
